import { BLOG_POSTS, blogPostUrl, getBlogPostNeighbors } from '../data/blogPosts';
import { ANCHOR_PRICE_USD, currentPassPriceUsd, freeMockLegalSummary, freeMockPublicLine } from '../constants/pricing';
import { homeWebApplicationSchema, pricingProductSchema } from './seoSchema';
import { canonicalUrl, SITE } from '../seo/urls';
import {
  LEVEL_HUB_SEO,
  LEVEL_PICKER_SEO,
  STUDY_GUIDE_HUB_DESCRIPTION,
  STUDY_GUIDE_HUB_TITLE,
  blogPostDocumentTitle,
  studyGuideDescription,
  studyGuideDocumentTitle,
} from '../seo/snippets';
import { HOME_FAQS, PRICING_FAQS, breadcrumbSchema, faqPageSchema } from '../data/seoFaqs';
import { BLOG_AUTHOR, blogPostingSchema } from '../seo/blogSchema';

const BLOG_URL = canonicalUrl('/blog');
const OG_IMAGE = `${SITE}/og.png`;

export { BLOG_AUTHOR };

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export { upsertMeta, upsertLink };

function removeLink(rel) {
  document.querySelector(`link[rel="${rel}"]`)?.remove();
}

function removeJsonLd(id) {
  document.getElementById(id)?.remove();
}

function injectJsonLd(id, data) {
  removeJsonLd(id);
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

/** Standard internal links shown on every blog post. */
export const BLOG_SITE_LINKS = [
  { href: '/pricing', label: 'Lifetime & monthly pricing' },
  { href: '/quiz/session-1', label: 'Try free Level 1 Mock 1' },
  { href: '/level/2', label: 'Level 2 mocks' },
  { href: '/level/3', label: 'Level 3 mocks' },
  { href: '/study-guide', label: 'Level 1 study guide' },
  { href: '/glossary?formulas=1', label: 'Level 1 formulas (searchable)' },
  { href: '/blog', label: 'All study guides' },
  { href: '/blog/free-cfa-level-1-mock-exam-2026', label: 'Free CFA Level 1 mock exam' },
  { href: '/blog/cfa-level-2-mock-exam-2026', label: 'Level 2 mock exam guide' },
  { href: '/blog/cfa-level-3-mock-exam-2026', label: 'Level 3 mock exam guide' },
  { href: '/blog/cfa-level-3-constructed-response-guide', label: 'Level 3 constructed response' },
  { href: '/blog/how-to-pass-cfa-level-1-study-plan', label: 'Level 1 study plan' },
];

export function applyBlogIndexSeo() {
  // Keep in sync with scripts/offer-copy.mjs BLOG_INDEX_TITLE / BLOG_INDEX_DESCRIPTION.
  document.title = 'CFA Study Blog: Mock Exams, Dates, Fees | CFA Mock Exam';
  upsertMeta(
    'name',
    'description',
    'CFA Level 1–3 study guides: mock exam and practice exam tips, exam dates, fees, formulas and study plans, plus a free 90-question Mock Exam 1.'
  );
  upsertLink('canonical', BLOG_URL);
  upsertMeta('property', 'og:title', 'CFA Study Blog (Levels 1–3) | CFA Mock Exam');
  upsertMeta(
    'property',
    'og:description',
    `Free CFA guides across levels: ${freeMockPublicLine().toLowerCase()}, Level 2 item-set mocks, Level 3 mixed sessions, dates, and study plans.`
  );
  upsertMeta('property', 'og:url', BLOG_URL);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('property', 'og:site_name', 'CFA Mock Exam');
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', 'CFA Study Blog (Levels 1–3) | CFA Mock Exam');
  upsertMeta(
    'name',
    'twitter:description',
    'CFA Level 1–3 guides: free Mock 1, item-set mocks, constructed response, dates, and study plans.'
  );
  upsertMeta('name', 'twitter:image', OG_IMAGE);

  injectJsonLd('blog-jsonld-blog', {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': `${BLOG_URL}#blog`,
    name: 'CFA Study Blog',
    url: BLOG_URL,
    description:
      'CFA Level 1–3 study guides — mock exams, format explainers, topic weights, and study plans.',
    publisher: { '@id': `${SITE}/#organization` },
    inLanguage: 'en',
  });

  injectJsonLd('blog-jsonld-collection', {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'CFA Level 1 Study Blog',
    url: BLOG_URL,
    isPartOf: { '@id': `${BLOG_URL}#blog` },
    publisher: { '@id': `${SITE}/#organization` },
  });

  injectJsonLd('blog-jsonld-itemlist', {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'CFA Level 1 study articles',
    numberOfItems: BLOG_POSTS.length,
    itemListElement: BLOG_POSTS.map((post, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: blogPostUrl(post.slug),
      name: post.title,
    })),
  });
}

export function applyBlogPostSeo(post) {
  if (!post) return () => {};

  const url = blogPostUrl(post.slug);
  const { prev, next } = getBlogPostNeighbors(post.slug);

  document.title = blogPostDocumentTitle(post.title);
  upsertMeta('name', 'description', post.metaDescription);
  upsertLink('canonical', url);

  if (prev) upsertLink('prev', blogPostUrl(prev.slug));
  else removeLink('prev');
  if (next) upsertLink('next', blogPostUrl(next.slug));
  else removeLink('next');

  upsertMeta('property', 'og:title', post.title);
  upsertMeta('property', 'og:description', post.metaDescription);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:type', 'article');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('property', 'og:site_name', 'CFA Mock Exam');
  upsertMeta('property', 'article:published_time', post.published);
  upsertMeta('property', 'article:modified_time', post.updated || post.published);
  upsertMeta('property', 'article:author', BLOG_AUTHOR.name);
  upsertMeta('property', 'article:section', 'CFA Level 1');
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', post.title);
  upsertMeta('name', 'twitter:description', post.metaDescription);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
  if (post.keywords?.length) {
    upsertMeta('name', 'keywords', post.keywords.join(', '));
  }

  // Same shape as the prerendered blog-jsonld-article / -breadcrumb / -faq
  // (src/seo/crawlerShell.js schemasForRoute); same ids, so these replace them.
  injectJsonLd('blog-jsonld-article', blogPostingSchema(post));
  injectJsonLd('blog-jsonld-breadcrumb', blogPostBreadcrumbSchema(post));

  const faqItems = blogPostFaqs(post);
  if (faqItems.length) {
    injectJsonLd('blog-jsonld-faq', faqPageSchema(faqItems));
  } else {
    removeJsonLd('blog-jsonld-faq');
  }

  return () => {
    removeLink('prev');
    removeLink('next');
    removeJsonLd('blog-jsonld-article');
    removeJsonLd('blog-jsonld-breadcrumb');
    removeJsonLd('blog-jsonld-faq');
  };
}

export function blogPostFaqs(post) {
  return [...(post.faqs || []), ...((post.sections || []).flatMap((s) => s.faqs || []))];
}

export function blogPostBreadcrumbSchema(post) {
  return breadcrumbSchema([
    { name: 'Blog', path: '/blog' },
    { name: post.title, path: `/blog/${post.slug}` },
  ]);
}

export function cleanupBlogSeo() {
  removeLink('prev');
  removeLink('next');
  removeJsonLd('blog-jsonld-blog');
  removeJsonLd('blog-jsonld-collection');
  removeJsonLd('blog-jsonld-itemlist');
  removeJsonLd('blog-jsonld-article');
  removeJsonLd('blog-jsonld-breadcrumb');
  removeJsonLd('blog-jsonld-faq');
}

/** Match public/index.html after blog routes overwrite title/description. */
/** Home title/description — keep literals in sync with scripts/offer-copy.mjs HOME_SEO_TITLE / HOME_SEO_DESCRIPTION. */
const HOME_DOCUMENT_TITLE = 'CFA Mock Exams: Free Level 1 Mock Test | CFA Mock Exam';
const HOME_DOCUMENT_DESCRIPTION =
  'Try free Level 1 Mock 1, no account needed: 90 questions and an instant score report. Paid access unlocks 8 timed mock exams from a 720+ question bank.';

export function applyHomeSeo() {
  const title = HOME_DOCUMENT_TITLE;
  const description = HOME_DOCUMENT_DESCRIPTION;
  document.title = title;
  upsertMeta('name', 'title', title);
  upsertMeta('name', 'description', description);
  upsertLink('canonical', `${SITE}/`);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', `${SITE}/`);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
  removeJsonLd('site-jsonld-product');
  removeJsonLd('site-jsonld-breadcrumb');
  injectJsonLd('site-jsonld-webapp', homeWebApplicationSchema());
  // Visible on the homepage FAQ (Home.js); same id as the prerendered block.
  injectJsonLd('site-jsonld-faq', faqPageSchema(HOME_FAQS));
  return () => removeJsonLd('site-jsonld-faq');
}

/** Keep title/description in sync with scripts/seo-meta.mjs PRICING_SEO (unit-tested). */
export function applyPricingSeo() {
  const title = 'CFA Mock Exam Pricing: Lifetime or Monthly Access';
  const description =
    'Compare lifetime and monthly access to 8 timed CFA Level 1 mock exams and 720+ practice exam questions. Try free Mock Exam 1 first, no card needed.';
  const url = canonicalUrl('/pricing');
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertLink('canonical', url);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
  removeJsonLd('site-jsonld-webapp');
  injectJsonLd('site-jsonld-product', pricingProductSchema());
  // Visible in the pricing FAQ (Pricing.js); same ids as the prerendered blocks.
  injectJsonLd('site-jsonld-faq', faqPageSchema(PRICING_FAQS));
  injectJsonLd('site-jsonld-breadcrumb', breadcrumbSchema([{ name: 'Pricing', path: '/pricing' }]));
  return () => {
    removeJsonLd('site-jsonld-faq');
    removeJsonLd('site-jsonld-breadcrumb');
  };
}

export function applyMockSessionsSeo() {
  const title = '8 Timed CFA Level 1 Mock Exams | CFA Mock Exam';
  // Keep in sync with scripts/seo-meta.mjs MOCK_SESSIONS_SEO.
  const description =
    '8 timed CFA Level 1 mock exams from a 720+ question bank, each a 90-question mock test at 2026 topic weights. Mock 1 is free; Mocks 2–8 need paid access.';
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertLink('canonical', canonicalUrl('/quiz/session'));
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonicalUrl('/quiz/session'));
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
}

/**
 * Mark the current client-rendered page noindex (crawlable, not indexable).
 * Returns a cleanup so the tag does not leak onto the next client-side route.
 * Keep in step with the X-Robots-Tag headers in netlify.toml (/login, /signup) and the
 * `robots` field in scripts/seo-meta.mjs (prerendered /quiz/l2, /quiz/l3).
 */
export function applyNoindexRobots(content = 'noindex, nofollow') {
  upsertMeta('name', 'robots', content);
  return () => {
    document.querySelector('meta[name="robots"]')?.remove();
  };
}

/** Same title/description as scripts/seo-meta.mjs /quiz/l2 (src/seo/snippets.js). */
export function applyMockSessionsL2Seo() {
  const { title, description } = LEVEL_PICKER_SEO.l2;
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertLink('canonical', canonicalUrl('/quiz/l2'));
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonicalUrl('/quiz/l2'));
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
  return applyNoindexRobots('noindex, follow');
}

/** Same title/description as scripts/seo-meta.mjs /quiz/l3 (src/seo/snippets.js). */
export function applyMockSessionsL3Seo() {
  const { title, description } = LEVEL_PICKER_SEO.l3;
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertLink('canonical', canonicalUrl('/quiz/l3'));
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonicalUrl('/quiz/l3'));
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
  return applyNoindexRobots('noindex, follow');
}

/**
 * Level 2 / Level 3 hub money pages. When ready, the title/description equal the
 * prerendered /level/2 and /level/3 snippets (src/seo/snippets.js LEVEL_HUB_SEO).
 */
export function applyLevelHubSeo({
  level = 'l2',
  label = 'Level 2',
  ready = true,
  priceUsd = 149,
  listUsd = 199,
  isFounding = true,
} = {}) {
  const path = level === 'l3' ? '/level/3' : '/level/2';
  const priceBit = isFounding
    ? `Founding $${priceUsd} lifetime (list $${listUsd}) or $29/mo`
    : `$${priceUsd} lifetime or $29/mo`;
  const hub = LEVEL_HUB_SEO[level === 'l3' ? 'l3' : 'l2'];
  const title = ready ? hub.title : `CFA ${label} mocks — coming soon | CFA Mock Exam`;
  const description = ready
    ? hub.description
    : `CFA ${label} timed mocks are in development. ${priceBit}. Live product today is Level 1.`;
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertLink('canonical', canonicalUrl(path));
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonicalUrl(path));
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
}

export function applyBillingSeo() {
  const title = 'Billing & access | CFA Mock Exam';
  const description =
    'View your CFA Mock Exam access, receipts, and Stripe billing portal. Lifetime is one payment; monthly can be canceled anytime.';
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertLink('canonical', `${SITE}/billing`);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', `${SITE}/billing`);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
}

/** Post-checkout claim page — keep out of search indexes. */
export function applyGetAccessSeo() {
  const title = 'Activate access | CFA Mock Exam';
  const description =
    'Link your Stripe receipt email to your account to unlock eight timed mocks, post-mock analysis, and explanations.';
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertMeta('name', 'robots', 'noindex, nofollow');
  upsertLink('canonical', `${SITE}/get-access`);
  return () => {
    document.querySelector('meta[name="robots"]')?.remove();
  };
}

export function applyStudyGuideSeo({ title, path, description: customDescription }) {
  const isHub = !path || path === '/study-guide';
  // Hub and default lesson text match scripts/seo-meta.mjs (src/seo/snippets.js).
  const pageTitle = isHub ? STUDY_GUIDE_HUB_TITLE : studyGuideDocumentTitle(title);
  const description = isHub
    ? STUDY_GUIDE_HUB_DESCRIPTION
    : customDescription || studyGuideDescription(title);
  const clipped = description.length > 160 ? `${description.slice(0, 157).trim()}…` : description;
  const url = canonicalUrl(path || '/study-guide');
  document.title = pageTitle;
  upsertMeta('name', 'description', clipped);
  upsertLink('canonical', url);
  upsertMeta('property', 'og:title', pageTitle);
  upsertMeta('property', 'og:description', clipped);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:type', 'article');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', pageTitle);
  upsertMeta('name', 'twitter:description', clipped);
  upsertMeta('name', 'twitter:image', OG_IMAGE);

  injectJsonLd('study-guide-jsonld', {
    '@context': 'https://schema.org',
    '@type': isHub ? 'CollectionPage' : 'LearningResource',
    name: title,
    description: clipped,
    url,
    isPartOf: {
      '@type': 'WebSite',
      name: 'CFA Mock Exam',
      url: `${SITE}/`,
    },
    about: {
      '@type': 'Thing',
      name: 'CFA Level 1',
    },
    educationalLevel: 'CFA Level I',
    learningResourceType: 'Study guide',
    inLanguage: 'en',
    publisher: {
      '@type': 'Organization',
      name: 'CFA Mock Exam',
      url: `${SITE}/`,
    },
  });
}

/** Simple static pages (About, Contact — Q8). Same tags as applyTermsSeo. */
export function applyStaticPageSeo({ path, title, description }) {
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertLink('canonical', canonicalUrl(path));
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonicalUrl(path));
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
}

export function applyTermsSeo() {
  const title = 'Terms of Use | CFA Mock Exam';
  const priceUsd = currentPassPriceUsd();
  const priceBit =
    priceUsd < ANCHOR_PRICE_USD
      ? `$${priceUsd} lifetime founding access (then $${ANCHOR_PRICE_USD})`
      : `$${ANCHOR_PRICE_USD} lifetime access`;
  const description = `Terms for CFA Mock Exam: ${freeMockLegalSummary()}, ${priceBit} or monthly plans, Stripe billing, and Wyoming governing law.`;
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertLink('canonical', canonicalUrl('/terms'));
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonicalUrl('/terms'));
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
}

export function applyGlossarySeo({ formulasOnly = false } = {}) {
  const title = formulasOnly
    ? 'CFA Level 1 Formulas | Glossary | CFA Mock Exam'
    : 'CFA Glossary & Level 1 Formula Sheet (130 Terms)';
  const description = formulasOnly
    ? 'Searchable CFA Level 1 formula sheet — TVM, duration, CAPM, WACC, ratios. Then sit free Mock 1 to apply them under the clock.'
    : 'Searchable CFA glossary: 130 Level 1 terms and 114 exam formulas (NPV, duration, CAPM, WACC, Sharpe ratio) across all ten topics. Free to browse, no sign-up.';
  const url = formulasOnly ? `${canonicalUrl('/glossary')}?formulas=1` : canonicalUrl('/glossary');
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertLink('canonical', canonicalUrl('/glossary'));
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
}

/** Placement page — keep out of search indexes (thin, not a money query). */
export function applyCareersSeo() {
  const title = 'CFA Jobs & Placement | CFA Mock Exam';
  const description =
    'Optional CFA-track placement after you sit Mock 1. Practice accounts are not a recruiting list unless you opt in.';
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertMeta('name', 'robots', 'noindex, nofollow');
  upsertLink('canonical', canonicalUrl('/careers'));
  return () => {
    document.querySelector('meta[name="robots"]')?.remove();
  };
}

export function applyPrivacySeo() {
  const title = 'Privacy Policy | CFA Mock Exam';
  const description =
    'How CFA Mock Exam collects and uses account data, quiz progress, Stripe payment references, cookies, and analytics. Contact support to request deletion.';
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertLink('canonical', canonicalUrl('/privacy'));
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonicalUrl('/privacy'));
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
}
